import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import { registerServiceWorker } from "./lib/pwa";
import { useServiceWorkerAuth, useServiceWorkerNavigation } from "./lib/offlineSync";
import InstallPWAPrompt from "./components/InstallPWAPrompt";
import OfflineIndicator from "./components/OfflineIndicator";
import SplashScreen from "./components/SplashScreen";
import "./App.css";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import OnboardingPage from "./pages/OnboardingPage";
import DashboardPage from "./pages/DashboardPage";
import TransactionsPage from "./pages/TransactionsPage";
import ObjectivesPage from "./pages/ObjectivesPage";
import BudgetsPage from "./pages/BudgetsPage";
import SettingsPage from "./pages/SettingsPage";
import ReferralPage from "./pages/ReferralPage";
import FamilyPage from "./pages/FamilyPage";
import ShoppingPage from "./pages/ShoppingPage";
import EmailScanPage from "./pages/EmailScanPage";
import ExportPage from "./pages/ExportPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import RecurringPage from "./pages/RecurringPage";
import CategoriesPage from "./pages/CategoriesPage";
import ReportsPage from "./pages/ReportsPage";
import PredictionsPage from "./pages/PredictionsPage";
import JoinPage from "./pages/JoinPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import ChatPage from "./pages/ChatPage";
import InvitationPage from "./pages/InvitationPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import InvoicesPage from "./pages/InvoicesPage";
import PrivacyPage from "./pages/PrivacyPage";
import HelpPage from "./pages/HelpPage";

// Admin Pages
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminPromosPage from "./pages/AdminPromosPage";
import AdminNotificationsPage from "./pages/AdminNotificationsPage";
import AdminAdminsPage from "./pages/AdminAdminsPage";
import AdminInvitationsPage from "./pages/AdminInvitationsPage";
import AdminSubscriptionsPage from "./pages/AdminSubscriptionsPage";
import AdminChallengesPage from "./pages/AdminChallengesPage";
import AdminCategoriesPage from "./pages/AdminCategoriesPage";
import AdminAIPromptsPage from "./pages/AdminAIPromptsPage";

// Register Service Worker
registerServiceWorker();

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // If user passed from AuthCallback, skip additional checks
  if (location.state?.user) {
    return children;
  }

  // Redirect to onboarding if not completed
  if (user && !user.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// App Router that handles Google OAuth callback
function AppRouter() {
  const location = useLocation();
  const [authProcessed, setAuthProcessed] = React.useState(false);

  // Check URL for Google OAuth callback (code parameter)
  // This handles the redirect from Google OAuth
  const searchParams = new URLSearchParams(location.search);
  const hasOAuthCode = searchParams.has('code') && location.pathname === '/auth/callback';
  
  // If we have an OAuth code and haven't processed it yet, show AuthCallback
  // This prevents the Route from also rendering AuthCallback
  if (hasOAuthCode && !authProcessed) {
    return <AuthCallback onProcessed={() => setAuthProcessed(true)} />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Auth callback - only shown if no code (error cases) or already processed */}
      <Route path="/auth/callback" element={
        authProcessed ? <AuthCallback /> : <LandingPage />
      } />
      
      {/* Join family via email link (public, handles redirect) */}
      <Route path="/join" element={<JoinPage />} />
      
      {/* Password Reset Flow (public) */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      
      {/* Admin Invitation (public) */}
      <Route path="/invitation/:invitationId" element={<InvitationPage />} />
      
      {/* Protected routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/transactions" element={
        <ProtectedRoute>
          <TransactionsPage />
        </ProtectedRoute>
      } />
      <Route path="/objectives" element={
        <ProtectedRoute>
          <ObjectivesPage />
        </ProtectedRoute>
      } />
      <Route path="/budgets" element={
        <ProtectedRoute>
          <BudgetsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/privacy" element={
        <ProtectedRoute>
          <PrivacyPage />
        </ProtectedRoute>
      } />
      <Route path="/help" element={
        <ProtectedRoute>
          <HelpPage />
        </ProtectedRoute>
      } />
      <Route path="/referral" element={
        <ProtectedRoute>
          <ReferralPage />
        </ProtectedRoute>
      } />
      <Route path="/family" element={
        <ProtectedRoute>
          <FamilyPage />
        </ProtectedRoute>
      } />
      <Route path="/shopping" element={
        <ProtectedRoute>
          <ShoppingPage />
        </ProtectedRoute>
      } />
      <Route path="/email-scan" element={
        <ProtectedRoute>
          <EmailScanPage />
        </ProtectedRoute>
      } />
      <Route path="/export" element={
        <ProtectedRoute>
          <ExportPage />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/recurring" element={
        <ProtectedRoute>
          <RecurringPage />
        </ProtectedRoute>
      } />
      <Route path="/subscriptions" element={
        <ProtectedRoute>
          <SubscriptionsPage />
        </ProtectedRoute>
      } />
      <Route path="/invoices" element={
        <ProtectedRoute>
          <InvoicesPage />
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />
      <Route path="/categories" element={
        <ProtectedRoute>
          <CategoriesPage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/predictions" element={
        <ProtectedRoute>
          <PredictionsPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <AdminUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/invitations" element={
        <ProtectedRoute>
          <AdminInvitationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/subscriptions" element={
        <ProtectedRoute>
          <AdminSubscriptionsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/promos" element={
        <ProtectedRoute>
          <AdminPromosPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/notifications" element={
        <ProtectedRoute>
          <AdminNotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/admins" element={
        <ProtectedRoute>
          <AdminAdminsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/challenges" element={
        <ProtectedRoute>
          <AdminChallengesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/categories" element={
        <ProtectedRoute>
          <AdminCategoriesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-prompts" element={
        <ProtectedRoute>
          <AdminAIPromptsPage />
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Initialize splash state synchronously to avoid flash
  const [showSplash, setShowSplash] = useState(() => {
    // Check sessionStorage synchronously during initial render
    if (typeof window !== 'undefined') {
      const hasSeenSplash = sessionStorage.getItem('spenspot_splash_shown');
      return !hasSeenSplash;
    }
    return true;
  });
  
  const handleSplashComplete = () => {
    sessionStorage.setItem('spenspot_splash_shown', 'true');
    setShowSplash(false);
  };
  
  return (
    <AuthProvider>
      <LanguageProvider>
        <ThemeProvider>
          {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
          <BrowserRouter>
            <AppWithServiceWorker />
          </BrowserRouter>
        </ThemeProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

// Wrapper component that has access to routing and auth
function AppWithServiceWorker() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // Provide auth token to service worker for background sync
  useServiceWorkerAuth(token);
  
  // Handle navigation requests from service worker (notification clicks)
  useServiceWorkerNavigation(navigate);
  
  return (
    <>
      <OfflineIndicator />
      <AppRouter />
      <InstallPWAPrompt />
      <Toaster position="top-center" richColors />
    </>
  );
}

export default App;
